import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { toast } = useToast();
  const navigate = useNavigate();
  const [state, setState] = useState<"loading" | "needs_auth" | "ready" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) {
        setState("error");
        setMessage("Missing invitation token.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        setState("needs_auth");
        return;
      }
      setState("ready");
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    const { data, error } = await supabase.rpc("accept_team_invitation" as any, { _token: token });
    setAccepting(false);
    if (error) {
      setState("error");
      setMessage(error.message);
      return;
    }
    setState("success");
    toast({ title: "Welcome to the team!", description: `You now have ${(data as any)?.role} access.` });
    setTimeout(() => navigate("/admin/dashboard"), 1500);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Team Invitation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === "loading" && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking invitation...
            </div>
          )}
          {state === "needs_auth" && (
            <>
              <p className="text-sm text-muted-foreground">
                Please sign in with the email address that received this invitation, then return to this link.
              </p>
              <Button className="w-full" onClick={() => navigate(`/admin/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}>
                Sign In
              </Button>
            </>
          )}
          {state === "ready" && (
            <>
              <p className="text-sm">You've been invited to join the team. Click below to accept and activate your role.</p>
              <Button className="w-full" disabled={accepting} onClick={accept}>
                {accepting ? "Accepting..." : "Accept Invitation"}
              </Button>
            </>
          )}
          {state === "success" && (
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5" /> Invitation accepted! Redirecting...
            </div>
          )}
          {state === "error" && (
            <div className="flex items-start gap-2 text-destructive">
              <AlertCircle className="h-5 w-5 mt-0.5" />
              <div className="text-sm">{message || "Failed to accept invitation."}</div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvite;
